import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { TicketListCard } from '../../components/TicketListCard'
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Breadcrumbs,
  Link,
  useTheme,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import {
  DataGrid,
  GridRenderCellParams
} from '@mui/x-data-grid'
import {
  LocationCity,
  ArrowBack,
  Edit,
  Email,
  CalendarToday,
  People,
  ConfirmationNumber,
  CheckCircle,
  Cancel,
  Add,
  MoreVert,
  MailOutline,
  LockReset,
  VerifiedUser,
  Delete,
  AutoAwesome,
  Link as LinkIcon
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { OrganizationService } from '../../../../shared/services/OrganizationService'
import { organisationsService } from '../../../../shared/services/OrganisationsService'
import { userService } from '../../../../shared/services/UserService'
import { apiClient } from '../../../../shared/services/ApiClient'
import { UserSummary, OrganizationType, InvitationSummary } from '../../../../shared/types/entities'

// Combined type for both users and invitations in the grid
type UserOrInvitation = (UserSummary & { type: 'user' }) | (InvitationSummary & { type: 'invitation' })

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`childsite-tabpanel-${index}`}
      aria-labelledby={`childsite-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

interface InviteUserFormData {
  email: string
  firstName: string
  lastName: string
  role: string
  message?: string
}

interface EditUserFormData {
  firstName: string
  lastName: string
  role: string
}

interface EditSiteFormData {
  name: string
  description?: string
  type: OrganizationType
  contactEmail?: string
  groupEmailAddress?: string
  useGroupEmailForNotifications?: boolean
  phoneNumber?: string
  address?: string
}

const inviteUserSchema = yup.object({
  email: yup.string().required('Email is required').email('Invalid email'),
  firstName: yup.string().required('First name is required').max(50, 'First name too long'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name too long'),
  role: yup.string().required('Role is required'),
  message: yup.string().max(500, 'Message too long')
})

const editUserSchema = yup.object({
  firstName: yup.string().required('First name is required').max(50, 'First name too long'),
  lastName: yup.string().required('Last name is required').max(50, 'Last name too long'),
  role: yup.string().required('Role is required')
})

const editSiteSchema: yup.ObjectSchema<EditSiteFormData> = yup.object({
  name: yup.string().required('Name is required').max(200, 'Name too long').defined(),
  description: yup.string().max(1000, 'Description too long').optional(),
  type: yup.mixed<OrganizationType>().oneOf(['Library', 'Department', 'Branch', 'Division', 'Other']).required('Type is required').defined(),
  contactEmail: yup.string().email('Invalid email').max(256, 'Email too long').optional(),
  groupEmailAddress: yup.string().email('Invalid email').max(256, 'Email too long').optional(),
  useGroupEmailForNotifications: yup.boolean().optional(),
  phoneNumber: yup.string().max(20, 'Phone number too long').optional(),
  address: yup.string().max(500, 'Address too long').optional()
}).defined()

function ChildSiteDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [childSite, setChildSite] = useState<any>(null)
  const [parentOrganization, setParentOrganization] = useState<any>(null)
  const [users, setUsers] = useState<UserOrInvitation[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState(false)
  const [selectedRole, setSelectedRole] = useState<string>('Customer')
  const [userMenuAnchor, setUserMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({})
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserSummary | null>(null)
  const [editSiteDialogOpen, setEditSiteDialogOpen] = useState(false)

  const {
    register: registerInvite,
    handleSubmit: handleSubmitInvite,
    formState: { errors: inviteErrors },
    reset: resetInvite
  } = useForm<InviteUserFormData>({
    resolver: yupResolver(inviteUserSchema),
    defaultValues: {
      role: 'Customer'
    }
  })

  const {
    register: registerEditUser,
    handleSubmit: handleSubmitEditUser,
    formState: { errors: editUserErrors },
    reset: resetEditUser,
    setValue: setEditUserValue
  } = useForm<EditUserFormData>({
    resolver: yupResolver(editUserSchema)
  })

  const {
    register: registerEditSite,
    handleSubmit: handleSubmitEditSite,
    formState: { errors: editSiteErrors },
    reset: resetEditSite,
    setValue: setEditSiteValue
  } = useForm<EditSiteFormData>({
    resolver: yupResolver(editSiteSchema)
  })

  useEffect(() => {
    if (id) {
      loadChildSite()
    }
  }, [id])

  useEffect(() => {
    // Load users when Users tab (index 1) is active
    if (tabValue === 1 && id) {
      loadUsers()
    }
  }, [tabValue, id])

  const loadChildSite = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await OrganizationService.getOrganization(id)
      setChildSite(data)

      // Load parent organization if this is a child site
      // Note: API returns British spelling 'organisationId' but TypeScript type uses American 'organizationId'
      const parentOrgId = (data as any).organisationId
      if (parentOrgId) {
        try {
          const parent = await organisationsService.getOrganisation(parentOrgId)
          setParentOrganization(parent)
        } catch (err) {
          console.error('Failed to load parent organization:', err)
        }
      } else {
        setParentOrganization(null)
      }
    } catch (err: any) {
      console.error('Failed to load child site:', err)
      setError(err.message || 'Failed to load child site details')
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    if (!id) return

    try {
      setUsersLoading(true)
      // Fetch both active users and pending invitations
      const [usersResponse, invitationsResponse] = await Promise.all([
        userService.getUsers(1, 100, undefined, undefined, undefined, id),
        userService.getPendingInvitations()
      ])

      // Map active users
      const activeUsers: UserOrInvitation[] = usersResponse.data.map(user => ({
        ...user,
        type: 'user' as const,
        id: user.userId // DataGrid requires an 'id' field
      }))

      // Filter and map pending invitations for this child site
      const pendingInvitations: UserOrInvitation[] = invitationsResponse
        .filter(inv => inv.organisationId === id)
        .map(inv => ({
          ...inv,
          type: 'invitation' as const,
          id: inv.invitationId,
          // Add placeholder fields for grid columns
          userId: inv.invitationId,
          isActive: false,
          emailVerified: false,
          createdAt: inv.invitedAt || new Date().toISOString(),
          role: inv.role
        }))

      // Combine active users and pending invitations
      setUsers([...activeUsers, ...pendingInvitations])
    } catch (err: any) {
      console.error('Failed to load users:', err)
    } finally {
      setUsersLoading(false)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const generateOrgSlug = (siteName: string) => {
    return siteName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
  }

  const handleInviteUser = async (data: InviteUserFormData) => {
    if (!id || !childSite) return

    try {
      setInviteError(null)

      await userService.inviteUser({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        message: data.message,
        customerId: childSite.organisationId,
        primaryOrganizationId: id,
        oversightChildSiteIds: undefined
      })

      setInviteSuccess(true)
      resetInvite()
      setSelectedRole('Customer')

      // Reload users list
      await loadUsers()

      // Close dialog after short delay
      setTimeout(() => {
        setInviteDialogOpen(false)
        setInviteSuccess(false)
      }, 1500)
    } catch (err: any) {
      console.error('Failed to invite user:', err)
      setInviteError(err.message || 'Failed to invite user')
    }
  }

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false)
    setInviteError(null)
    setInviteSuccess(false)
    setSelectedRole('Customer')
    resetInvite()
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserSummary) => {
    setUserMenuAnchor(prev => ({ ...prev, [user.userId]: event.currentTarget }))
  }

  const handleUserMenuClose = (userId: string) => {
    setUserMenuAnchor(prev => ({ ...prev, [userId]: null }))
  }

  const handleResendInvitation = async (user: UserSummary) => {
    try {
      await userService.resendInvitation(user.email)
      alert(`Invitation resent successfully to ${user.email}`)
    } catch (err: any) {
      console.error('Failed to resend invitation:', err)
      alert(`Failed to resend invitation: ${err.message || 'Please try again'}`)
    }
    handleUserMenuClose(user.userId)
  }

  const handleResetPassword = async (user: UserSummary) => {
    try {
      await userService.resetPassword(user.email)
      alert(`Password reset link sent successfully to ${user.email}`)
    } catch (err: any) {
      console.error('Failed to send password reset:', err)
      alert(`Failed to send password reset: ${err.message || 'Please try again'}`)
    }
    handleUserMenuClose(user.userId)
  }

  const handleVerifyEmail = async (user: UserSummary) => {
    try {
      await apiClient.post('/auth/admin/verify-user-email', { email: user.email })
      alert(`Email verified successfully for ${user.email}`)
      await loadUsers()
    } catch (err) {
      console.error('Failed to verify email:', err)
      alert('Failed to verify email. Please try again.')
    }
    handleUserMenuClose(user.userId)
  }

  const handleDeleteUser = async (user: UserSummary) => {
    if (!window.confirm(`Are you sure you want to delete ${user.fullName}? This action cannot be undone.`)) {
      handleUserMenuClose(user.userId)
      return
    }

    try {
      await userService.deleteUser(user.userId)
      alert(`User ${user.fullName} deleted successfully`)
      await loadUsers() // Reload user list to reflect deletion
    } catch (err: any) {
      console.error('Failed to delete user:', err)
      alert(`Failed to delete user: ${err.message || 'Please try again'}`)
    }
    handleUserMenuClose(user.userId)
  }

  const handleOpenEditUser = (user: UserSummary) => {
    setSelectedUserForEdit(user)
    setEditUserValue('firstName', user.firstName)
    setEditUserValue('lastName', user.lastName)
    setEditUserValue('role', user.role)
    setEditUserDialogOpen(true)
    handleUserMenuClose(user.userId)
  }

  const handleCloseEditUser = () => {
    setEditUserDialogOpen(false)
    setSelectedUserForEdit(null)
    resetEditUser()
  }

  const handleEditUserSubmit = async (data: EditUserFormData) => {
    if (!selectedUserForEdit) return

    try {
      await userService.updateUser(selectedUserForEdit.userId, {
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        isActive: selectedUserForEdit.isActive
      })
      alert(`User ${data.firstName} ${data.lastName} updated successfully`)
      await loadUsers() // Reload user list to reflect changes
      handleCloseEditUser()
    } catch (err: any) {
      console.error('Failed to update user:', err)
      alert(`Failed to update user: ${err.message || 'Please try again'}`)
    }
  }

  const handleOpenEditSite = () => {
    if (!childSite) return

    // Pre-populate form with current child site data
    setEditSiteValue('name', childSite.name)
    setEditSiteValue('description', childSite.description || '')
    setEditSiteValue('type', childSite.type)
    setEditSiteValue('contactEmail', childSite.contactEmail || '')
    setEditSiteValue('groupEmailAddress', childSite.groupEmailAddress || '')
    setEditSiteValue('useGroupEmailForNotifications', childSite.useGroupEmailForNotifications || false)
    setEditSiteValue('phoneNumber', childSite.phoneNumber || '')
    setEditSiteValue('address', childSite.address || '')

    setEditSiteDialogOpen(true)
  }

  const handleCloseEditSite = () => {
    setEditSiteDialogOpen(false)
    resetEditSite()
  }

  const handleEditSiteSubmit = async (data: EditSiteFormData) => {
    if (!childSite || !id) return

    try {
      await OrganizationService.updateOrganization(id, data)
      alert(`Site ${data.name} updated successfully`)
      await loadChildSite() // Reload site to reflect changes
      handleCloseEditSite()
    } catch (err: any) {
      console.error('Failed to update site:', err)
      alert(`Failed to update site: ${err.message || 'Please try again'}`)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !childSite) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Child site not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/organizations')}
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Organisations
        </Link>
        {parentOrganization && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/admin/organizations/${parentOrganization.customerId}`)}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {parentOrganization.companyName}
          </Link>
        )}
        <Typography color="text.primary">{childSite.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LocationCity fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {childSite.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={childSite.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={childSite.isActive ? 'success' : 'default'}
                icon={childSite.isActive ? <CheckCircle /> : <Cancel />}
              />
              <Chip label={childSite.type} size="small" variant="outlined" />
            </Box>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={handleOpenEditSite}
        >
          Edit Site
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <People color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {childSite.userCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Site members
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ConfirmationNumber color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Total Tickets
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {childSite.ticketCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {childSite.openTicketCount} open
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarToday color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {new Date(childSite.createdAt).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Users" />
          <Tab label="Tickets" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {childSite.contactEmail && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Contact Email
                        </Typography>
                        <Typography variant="body2">{childSite.contactEmail}</Typography>
                      </Box>
                    </Box>
                  )}
                  {childSite.emailAddress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoAwesome color="success" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Support Ticket Email
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.813rem' }}>
                          {childSite.emailAddress}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon color="primary" />
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Customer Portal Link
                      </Typography>
                      <Link
                        href={`https://helpdesk.taylindigital.com/customer?org=${generateOrgSlug(childSite.name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.813rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        https://helpdesk.taylindigital.com/customer?org={generateOrgSlug(childSite.name)}
                      </Link>
                    </Box>
                  </Box>
                  {((childSite as any).addressLine1 || (childSite as any).city || (childSite as any).country) && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationCity color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Address
                        </Typography>
                        <Typography variant="body2">
                          {(childSite as any).addressLine1 && <>{(childSite as any).addressLine1}<br /></>}
                          {(childSite as any).addressLine2 && <>{(childSite as any).addressLine2}<br /></>}
                          {(childSite as any).city && <>{(childSite as any).city}{(childSite as any).stateCounty ? ', ' : ''}</>}
                          {(childSite as any).stateCounty && <>{(childSite as any).stateCounty} </>}
                          {(childSite as any).postalCode && <>{(childSite as any).postalCode}<br /></>}
                          {(childSite as any).country}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {childSite.description && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {childSite.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Users Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Users
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite User
          </Button>
        </Box>

        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <People sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Users
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This site doesn't have any users yet.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={users.map((item: UserOrInvitation) => {
                if (item.type === 'user') {
                  return {
                    id: item.userId,
                    userId: item.userId,
                    fullName: item.fullName || `${item.firstName} ${item.lastName}`,
                    email: item.email,
                    role: item.role,
                    lastLoginAt: item.lastLoginAt,
                    isActive: item.isActive,
                    emailVerified: item.emailVerified,
                    assignedTickets: item.assignedTicketCount || 0,
                    openTickets: item.openTicketCount || 0,
                    type: 'user',
                    user: item  // Keep reference to full user object for actions
                  }
                } else {
                  return {
                    id: item.invitationId,
                    userId: item.invitationId,
                    fullName: `${item.firstName} ${item.lastName}`,
                    email: item.email,
                    role: item.role,
                    lastLoginAt: null,
                    isActive: false,
                    emailVerified: false,
                    assignedTickets: 0,
                    openTickets: 0,
                    type: 'invitation',
                    user: item  // Keep reference to full invitation object for actions
                  }
                }
              })}
              columns={[
                {
                  field: 'fullName',
                  headerName: 'Name',
                  flex: 1,
                  minWidth: 200,
                  renderCell: (params: GridRenderCellParams) => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {params.row.fullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2)}
                      </Box>
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 600 }}
                        >
                          {params.row.fullName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: 'block', mt: 0.25 }}
                        >
                          {params.row.email}
                        </Typography>
                      </Box>
                    </Box>
                  )
                },
                {
                  field: 'role',
                  headerName: 'Role',
                  width: 140,
                  renderCell: (params: GridRenderCellParams) => (
                    <Chip
                      label={params.row.role}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                  )
                },
                {
                  field: 'lastLoginAt',
                  headerName: 'Last Login',
                  flex: 1,
                  minWidth: 150,
                  renderCell: (params: GridRenderCellParams) => {
                    if (params.row.type === 'invitation') {
                      return (
                        <Chip
                          label="Pending"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )
                    }
                    return (
                      <Typography variant="body2" noWrap>
                        {params.row.lastLoginAt
                          ? new Date(params.row.lastLoginAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'Never'}
                      </Typography>
                    )
                  }
                },
                {
                  field: 'assignedTickets',
                  headerName: 'Assigned',
                  width: 100,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ConfirmationNumber sx={{ fontSize: 16, color: theme.palette.grey[500] }} />
                      <Typography variant="body2">{params.row.assignedTickets}</Typography>
                    </Box>
                  )
                },
                {
                  field: 'isActive',
                  headerName: 'Status',
                  width: 110,
                  renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={params.row.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={params.row.isActive ? 'success' : 'default'}
                        icon={params.row.isActive ? <CheckCircle /> : <Cancel />}
                        sx={{ fontWeight: 500 }}
                      />
                      {!params.row.emailVerified && (
                        <Chip
                          label="Unverified"
                          size="small"
                          color="warning"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                    </Box>
                  )
                },
                {
                  field: 'actions',
                  headerName: 'Actions',
                  width: 80,
                  sortable: false,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params: GridRenderCellParams) => (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUserMenuOpen(e, params.row.user)
                        }}
                        sx={{
                          color: theme.palette.grey[600],
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={userMenuAnchor[params.row.userId]}
                        open={Boolean(userMenuAnchor[params.row.userId])}
                        onClose={() => handleUserMenuClose(params.row.userId)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      >
                        <MenuItem onClick={() => handleOpenEditUser(params.row.user)}>
                          <ListItemIcon>
                            <Edit fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Edit User</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleResendInvitation(params.row.user)}>
                          <ListItemIcon>
                            <MailOutline fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Resend Invitation</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleResetPassword(params.row.user)}>
                          <ListItemIcon>
                            <LockReset fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Reset Password</ListItemText>
                        </MenuItem>
                        {!params.row.emailVerified && (
                          <MenuItem onClick={() => handleVerifyEmail(params.row.user)}>
                            <ListItemIcon>
                              <VerifiedUser fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Verify Email</ListItemText>
                          </MenuItem>
                        )}
                        <Divider />
                        <MenuItem onClick={() => handleDeleteUser(params.row.user)} sx={{ color: 'error.main' }}>
                          <ListItemIcon>
                            <Delete fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText>Delete User</ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  )
                }
              ]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                }
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  cursor: 'pointer'
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `2px solid ${theme.palette.divider}`
                }
              }}
            />
          </Box>
        )}
      </TabPanel>

      {/* Tickets Tab */}
      <TabPanel value={tabValue} index={2}>
        <TicketListCard organizationId={id} variant="admin" />
      </TabPanel>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog} maxWidth="md" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <form onSubmit={handleSubmitInvite(handleInviteUser)}>
          <DialogContent>
            {inviteError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInviteError(null)}>
                {inviteError}
              </Alert>
            )}

            {inviteSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                User invitation sent successfully!
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  placeholder="John"
                  {...registerInvite('firstName')}
                  error={!!inviteErrors.firstName}
                  helperText={inviteErrors.firstName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  placeholder="Doe"
                  {...registerInvite('lastName')}
                  error={!!inviteErrors.lastName}
                  helperText={inviteErrors.lastName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  placeholder="john.doe@example.com"
                  {...registerInvite('email')}
                  error={!!inviteErrors.email}
                  helperText={inviteErrors.email?.message}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!inviteErrors.role}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...registerInvite('role')}
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="Customer">Customer</MenuItem>
                  </Select>
                  {inviteErrors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {inviteErrors.role.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Message (Optional)"
                  placeholder="Add a personal message to the invitation email..."
                  {...registerInvite('message')}
                  error={!!inviteErrors.message}
                  helperText={inviteErrors.message?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseInviteDialog} disabled={inviteSuccess}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={inviteSuccess}>
              Send Invitation
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onClose={handleCloseEditUser} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <form onSubmit={handleSubmitEditUser(handleEditUserSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  {...registerEditUser('firstName')}
                  error={!!editUserErrors.firstName}
                  helperText={editUserErrors.firstName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  {...registerEditUser('lastName')}
                  error={!!editUserErrors.lastName}
                  helperText={editUserErrors.lastName?.message}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={selectedUserForEdit?.email || ''}
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!editUserErrors.role}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...registerEditUser('role')}
                    defaultValue={selectedUserForEdit?.role || 'Customer'}
                    label="Role"
                  >
                    <MenuItem value="Customer">Customer</MenuItem>
                    <MenuItem value="Oversight">Oversight</MenuItem>
                    <MenuItem value="Agent">Agent</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                  </Select>
                  {editUserErrors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {editUserErrors.role.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditUser}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Site Dialog */}
      <Dialog open={editSiteDialogOpen} onClose={handleCloseEditSite} maxWidth="md" fullWidth>
        <DialogTitle>Edit Site</DialogTitle>
        <form onSubmit={handleSubmitEditSite(handleEditSiteSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Name"
                  {...registerEditSite('name')}
                  error={!!editSiteErrors.name}
                  helperText={editSiteErrors.name?.message}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth error={!!editSiteErrors.type}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    {...registerEditSite('type')}
                    defaultValue={childSite?.type || 'Department'}
                    label="Type"
                  >
                    <MenuItem value="Library">Library</MenuItem>
                    <MenuItem value="Department">Department</MenuItem>
                    <MenuItem value="Branch">Branch</MenuItem>
                    <MenuItem value="Division">Division</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {editSiteErrors.type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {editSiteErrors.type.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description (Optional)"
                  {...registerEditSite('description')}
                  error={!!editSiteErrors.description}
                  helperText={editSiteErrors.description?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Email (Optional)"
                  type="email"
                  {...registerEditSite('contactEmail')}
                  error={!!editSiteErrors.contactEmail}
                  helperText={editSiteErrors.contactEmail?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Group Email Address (Optional)"
                  type="email"
                  {...registerEditSite('groupEmailAddress')}
                  error={!!editSiteErrors.groupEmailAddress}
                  helperText={editSiteErrors.groupEmailAddress?.message || "Shared mailbox for group ticket notifications (e.g., team@example.com)"}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...registerEditSite('useGroupEmailForNotifications')}
                      defaultChecked={false}
                    />
                  }
                  label="Use Group Email for Notifications"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                  When enabled, group ticket notifications will be sent to the group email address instead of individual staff members
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  {...registerEditSite('phoneNumber')}
                  error={!!editSiteErrors.phoneNumber}
                  helperText={editSiteErrors.phoneNumber?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address (Optional)"
                  {...registerEditSite('address')}
                  error={!!editSiteErrors.address}
                  helperText={editSiteErrors.address?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditSite}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}

export default ChildSiteDetail
